import { DropDown } from "@/comps/dropdown"
import { CustomThemedResource } from "@/comps/image"
import { assetUrl } from "@/utils/cdn"
import clsx from "clsx"
import { SessionStatus } from "controllers/dashboard"
import { useTranslation } from "next-i18next"

export type ChangeNameType = { name: string; id: number }

interface SessionItemProps {
	changeName: ChangeNameType | null
	setChangeName: (v: ChangeNameType | null) => void
	selectSession: (id: number) => Promise<void>
	setDeleteSessionPopup: (v: number) => void
	id: number
	name: string
	selectedSessionId: number | null
	status: SessionStatus
	active: boolean
	setActive: (id: number | null) => void
	remakeSession: (id: number) => Promise<void>
	duplicateSession: (id: number) => Promise<void>
	onClick: () => void
}

export function SessionItem(props: SessionItemProps) {
	const {
		id,
		active,
		setActive,
		name,
		changeName,
		setChangeName,
		selectedSessionId,
		selectSession,
		status,
		setDeleteSessionPopup,
		remakeSession,
		onClick,
	} = props

	const { t } = useTranslation()
	const selected = selectedSessionId === id
	const tryChangeName =
		changeName !== null && changeName.id === id

	return (
		<div
			onClick={async () => {
				if (selected || changeName !== null) {
					return
				}

				await selectSession(id)
				onClick()
			}}
			className={clsx(
				selected ? "bg-blue-100" : "bg-[unset]",
				tryChangeName
					? "cursor-default border-2 border-blue-400"
					: "group cursor-pointer hover:bg-blue-100",
				"flex w-full justify-between rounded-[6px]",
				"relative animate-opacityOnce items-center pr-[10px]",
				active ? "z-20" : "z-10",
			)}>
			<div
				id={`session-container-${id}`}
				className={clsx(
					tryChangeName ? "p-[8px]" : "p-[10px]",
					"flex items-center gap-[8px]",
					"max-h-[46px] min-h-[46px]",
				)}>
				<img
					src={assetUrl("/general/chat-icon.svg")}
					alt="chat icon"
					className="h-[20px] w-[20px]"
				/>
				{tryChangeName ? (
					<input
						value={changeName.name}
						onClick={(e) => e.stopPropagation()}
						autoFocus
						onChange={(e) => {
							const value = e.currentTarget.value
							if (value.length <= 100) {
								setChangeName({
									...changeName,
									name: e.currentTarget.value,
								})
							}
						}}
						className={clsx(
							"border-none bg-[unset] outline-none",
							"text-[14px] font-500",
						)}
					/>
				) : (
					<p
						className={clsx(
							selected
								? "desktop:max-w-[110px]"
								: "desktop:max-w-[130px]",
							"desktop:block group-hover:desktop:max-w-[110px]",
							"text-[14px] font-500 text-blue-700 max-w-[150px] truncate",
							"tablet:max-w-[110px]"
						)}>
						{name}
					</p>
				)}
			</div>

			<div className="flex items-center gap-[6px]">
				{status !== "idle" && status !== "deleted" && (
					<div
						className={clsx(
							tryChangeName && "hidden",
							selected
								? "flex group-hover:flex"
								: "desktop:group-hover:hidden",
							"flex h-3 w-3 items-center justify-center tablet:h-[24px] tablet:w-[24px]",
						)}>
						{getStatusIcon(status)}
					</div>
				)}

				<DropDown
					closeOnClick
					closeInSelect
					trigger={
						<div className={clsx("h-[21px] shrink-0")}>
							<div
								onClick={() => setActive(id)}
								className={clsx(
									tryChangeName && "desktop:hidden",
									!selected
										? "group-hover:flex desktop:hidden"
										: "flex",
									"flex h-6 w-6 shrink-0 rotate-90 items-center justify-center",
								)}>
								<img
									src={assetUrl("/general/more-gray.svg")}
									alt="more icon"
									className={"h-[18px] w-[18px] shrink-0"}
								/>
							</div>
						</div>
					}>
					<div
						className={clsx(
							"z-50 flex flex-col gap-[4px] p-[10px]",
							"absolute rounded-[10px] bg-color-popup-cell",
							"right-[0px] top-[50px] tablet:top-[40px]",
							"w-[160px] border border-color-separator",
						)}>
						<button
							onClick={() => {
								setActive(null)
								setChangeName({
									id,
									name,
								})
							}}
							className={clsx(
								"rounded-[8px] p-[12px] hover:bg-blue-100",
								"flex h-[44px] w-full items-center gap-[6px]",
							)}>
							<img
								src={assetUrl("/general/edit.svg")}
								alt="edit icon"
								className="h-[20px] w-[20px]"
							/>
							<span className="text-[14px] font-500 text-blue-800">
								{t("txt_rename")}
							</span>
						</button>
						{/* <button
          onClick={(e) => {
            handleMobileRenameDelete(e, id, false)
            setActive(null)
            duplicateSession(id)
          }}
          className={clsx(
            status === "completed" ? "flex" : "hidden",
            "rounded-[8px] p-[12px] hover:bg-blue-100",
            "h-[44px] w-[140px] items-center gap-[6px]",
          )}>
          <CustomThemedResource
            source="/general/duplicate"
            format="png?aaa"
            alt="duplicate icon"
            className="h-[20px] w-[20px]"
          />
          <span className="text-[14px] font-500 text-blue-800">
            {t("lbl_duplicate")}
          </span>
        </button> */}
						<button
							onClick={() => {
								setActive(null)
								remakeSession(id)
							}}
							className={clsx(
								status === "completed" ? "flex" : "hidden",
								"rounded-[8px] p-[12px] hover:bg-blue-100",
								"h-[44px] w-full items-center gap-[6px]",
							)}>
							<img
								src={assetUrl("/general/remake.png")}
								alt="remake icon"
								className="h-[20px] w-[20px]"
							/>
							<span className="text-[14px] font-500 text-blue-800">
								{t("txt_remake_video")}
							</span>
						</button>

						<button
							onClick={() => {
								setActive(null)
								setDeleteSessionPopup(id)
							}}
							className={clsx(
								"rounded-[8px] p-[12px] hover:bg-blue-100",
								"flex h-[44px] w-full items-center gap-[6px]",
							)}>
							<img
								src={assetUrl("/general/trash-red.png")}
								alt="edit icon"
								className="h-[20px] w-[20px]"
							/>
							<span className="text-[14px] font-500 text-color-error">
								{t("lbl_delete")}
							</span>
						</button>
					</div>
				</DropDown>
			</div>
		</div>
	)
}

function getStatusIcon(status: SessionStatus): JSX.Element {
	switch (status) {
		case "deleted":
		case "idle":
			return <></>
		case "analyzing":
			return (
				<img
					src={assetUrl("/general/analyzing.webp")}
					alt="analyzing icon"
					width={24}
					height={24}
				/>
			)
		case "visualizing":
			return (
				<img
					src={assetUrl("/general/visualizing.webp")}
					alt="visualizing icon"
					width={24}
					height={24}
				/>
			)
		case "generating":
			return (
				<CustomThemedResource
					source="/general/generating"
					format="webp"
					className="h-[24px] w-[24px]"
				/>
			)
		case "completed":
			return (
				<div className="h-6 w-6 [--icon-color:var(--color-blue-400)]">
					{CompletedIcon()}
				</div>
			)
		case "error":
		case "retryable_error":
			return (
				<div className="h-[6px] w-[6px] rounded-full bg-color-error" />
			)
		case "waiting":
			return (
				<div className="h-[6px] w-[6px] rounded-full bg-color-success" />
			)
		case "client-loading":
		case "loading":
			return (
				<div
					className={clsx(
						"flex gap-[2px] tablet:gap-1",
						"items-center",
					)}>
					<div
						className={clsx(
							"h-[4px] w-[4px] animate-loading rounded-full bg-blue-400 [animation-delay:0.1s] tablet:h-[6px] tablet:w-[6px]",
						)}
					/>
					<div
						className={clsx(
							"h-[4px] w-[4px] animate-loading rounded-full bg-blue-400 [animation-delay:0.2s] tablet:h-[6px] tablet:w-[6px]",
						)}
					/>
					<div
						className={clsx(
							"h-[4px] w-[4px] animate-loading rounded-full bg-blue-400 [animation-delay:0.3s] tablet:h-[6px] tablet:w-[6px]",
						)}
					/>
				</div>
			)
	}
}

const CompletedIcon = () => {
	return (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				d="M20.3996 6L7.71389 18L3.59961 13.2"
				stroke="var(--icon-color)"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
